import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { getAuth } from "firebase/auth";
import * as db from "../../firebase/tasks";
import { TaskRow } from "../../components/tms/TaskRow";
import TaskSearchInput from "../../components/tms/TaskSearchInput";

/**
 * LabelFilterView Component
 * Renders the view shown when a user clicks a label tag in the task list.
 */
export function LabelFilterView() {
  const { labelName: encodedLabelName } = useParams();
  const labelName = decodeURIComponent(encodedLabelName || "");
  
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [allLabels, setAllLabels] = useState([]);
  const unsubsRef = useRef([]);

  const clearUnsubs = () => {
    unsubsRef.current.forEach((unsub) => {
      if (typeof unsub === "function") {
        unsub();
      }
    });
    unsubsRef.current = [];
  };

  // Fetch users and subscribe to labels on mount
  useEffect(() => {
    let active = true;
    let unsubLabels = null;

    const loadMetadata = async () => {
      try {
        const fetchedUsers = await db.listTaskUsers().catch(err => {
          console.error("listTaskUsers error in LabelFilterView", err);
          return [];
        });
        if (!active) return;
        setUsers(fetchedUsers);

        unsubLabels = db.subscribeToLabels((updatedLabels) => {
          if (active) setAllLabels(updatedLabels);
        });
      } catch (err) {
        console.error("Error loading metadata in LabelFilterView:", err);
      }
    };

    loadMetadata();

    return () => {
      active = false;
      if (unsubLabels) unsubLabels();
    };
  }, []);

  useEffect(() => {
    let active = true;

    const fetchTasks = async () => {
      setLoading(true);
      try {
        const fetchedTasks = await db.getTasksByLabel(labelName);
        if (!active) return;

        // Sort tasks by taskNumber descending
        const sorted = fetchedTasks.sort((a, b) => b.taskNumber - a.taskNumber);
        setTasks(sorted);
        setLoading(false);

        clearUnsubs();

        // Subscribe to real-time updates for each task
        const newUnsubs = sorted.map((task) => {
          return db.subscribeToTask(task.id, (updatedTask) => {
            if (!active) return;
            if (updatedTask) {
              // If the task no longer contains the target label, remove it from the list
              const hasLabel = (updatedTask.labels || []).includes(labelName);
              if (!hasLabel) {
                setTasks((prev) => prev.filter((pt) => pt.id !== updatedTask.id));
              } else {
                setTasks((prev) =>
                  prev.map((pt) => (pt.id === updatedTask.id ? { ...pt, ...updatedTask } : pt))
                );
              }
            } else {
              // Task deleted
              setTasks((prev) => prev.filter((pt) => pt.id !== task.id));
            }
          });
        });

        unsubsRef.current = newUnsubs;
      } catch (err) {
        console.error("Error loading tasks by label:", err);
        if (active) setLoading(false);
      }
    };

    if (labelName) {
      fetchTasks();
    }

    return () => {
      active = false;
      clearUnsubs();
    };
  }, [labelName]);

  const auth = getAuth();
  const currentUser = auth.currentUser;

  const derivedAllLabels = Array.from(new Set([
    ...allLabels.map(l => l.name.toLowerCase()),
    ...tasks.flatMap(t => (t.labels || []).map(l => l.toLowerCase()))
  ])).map(name => {
    const existing = allLabels.find(l => l.name.toLowerCase() === name);
    return existing || { id: name, name: name, color: "#6b7280" };
  });

  return (
    <div className="min-h-screen bg-white py-10 px-4 sm:px-6 md:px-8 max-w-3xl mx-auto">
      {/* Header Container */}
      <div className="flex flex-col gap-1 mb-6">
        {/* Breadcrumb Row */}
        <div className="text-sm text-gray-400 font-semibold select-none">
          <Link to="/tasks" className="hover:underline text-gray-400 no-underline">
            Filters & Labels
          </Link>{" "}
          /
        </div>

        {/* Title & Search Row */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {labelName}
          </h1>
          <TaskSearchInput />
        </div>
      </div>

      {/* Task List Content */}
      {loading ? (
        <ul className="space-y-0 border-t border-gray-100">
          {Array.from({ length: 3 }).map((_, idx) => (
            <li key={idx} className="flex items-start gap-3 py-3 px-4 border-b border-gray-100 animate-pulse">
              <div className="w-5 h-5 rounded-full border-2 border-gray-200 shrink-0 mt-0.5" />
              <div className="flex-1 space-y-2 mt-1">
                <div className="h-4 bg-gray-200 rounded w-1/3 animate-pulse" />
                <div className="h-3 bg-gray-200 rounded w-2/3 animate-pulse" />
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <>
          {/* List styling includes the hover / absolute pseudo-element logic to show "Inbox 📥" */}
          <ul className="space-y-0 border-t border-gray-100 -mx-4 sm:mx-0 [&_.cursor-grab]:hidden lg:[&_.cursor-grab]:inline-block [&_.group-hover\:visible]:visible lg:[&_.group-hover\:visible]:invisible lg:group-hover:[&_.group-hover\:visible]:visible [&_.mt-1\.5>div]:w-full lg:[&_.mt-1\.5>div]:w-auto [&>li]:after:content-['Inbox_📥'] [&>li]:after:text-sm [&>li]:after:text-gray-400 [&>li]:after:absolute [&>li]:after:right-4 [&>li]:after:top-1/2 [&>li]:after:-translate-y-1/2 [&>li]:after:pointer-events-none [&>li]:after:transition-opacity [&>li]:hover:after:opacity-0">
            {tasks.map((task) => (
              <TaskRow 
                key={task.id} 
                task={task} 
                users={users} 
                allLabels={derivedAllLabels} 
                setAllLabels={setAllLabels} 
                currentUser={currentUser} 
              />
            ))}
          </ul>

          {tasks.length === 0 && (
            <div className="py-12 text-center select-none">
              <p className="text-gray-400 text-sm font-medium">No tasks with this label</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default LabelFilterView;
